import {
    hasBuildingPermission,
    hasOrgPermission,
    type CurrentUser,
    type HasPermissionType,
} from '@quipex/core/auth';
import type { CurrentUserBuildingPermissionResponse } from '@quipex/shared/contracts';
import type { AnimatedIconName } from '../../components/animated-icon/models';

// --- Menu modeling types ---
export type DeviceTarget = 'desktop' | 'mobile' | 'both';

export interface MenuBase {
    readonly key: string;
    readonly orgPermissions?: HasPermissionType;
    readonly buildingPermissions?: HasPermissionType;
    readonly device?: DeviceTarget;
    /** Visibility predicate. If returns false, hide. If true/undefined, show. */
    readonly visibility?: () => boolean | undefined;
}

export interface NavHeader extends MenuBase {
    readonly kind: 'header';
    readonly label: string;
}

export interface NavLink extends MenuBase {
    readonly kind: 'link';
    readonly label: string;
    readonly route: string | string[];
    readonly icon: string;
    readonly className?: string;
    readonly onClick?: () => void;
    readonly exact?: boolean;
}

interface NavButtonBase extends MenuBase {
    readonly kind: 'button';
    readonly label: string;
    readonly onClick: () => void;
    readonly className?: string;
    readonly badge?: string | number;
    readonly badgeMode?: 'count' | 'dot';
    readonly badgeDescription?: string;
    readonly ariaControls?: string;
    readonly ariaExpanded?: boolean;
    readonly ariaHasPopup?:
        | boolean
        | 'dialog'
        | 'grid'
        | 'listbox'
        | 'menu'
        | 'tree';
    readonly ariaLabel?: string;
}

type NavButtonIcon =
    | {
          readonly icon: string;
          readonly animatedIcon?: never;
      }
    | {
          readonly icon?: never;
          readonly animatedIcon: {
              readonly name: AnimatedIconName;
              readonly active: boolean;
          };
      };

export type NavButton = NavButtonBase & NavButtonIcon;

export type NavItem = NavHeader | NavLink | NavButton;

export interface NavAccessContext {
    readonly user?: CurrentUser | null;
    readonly buildingPermission?: CurrentUserBuildingPermissionResponse | null;
}

export function hasAccessToNavItem(
    item: MenuBase,
    context: NavAccessContext = {}
): boolean {
    const orgPermOk =
        item.orgPermissions !== undefined
            ? hasOrgPermission(context.user ?? null, item.orgPermissions)
            : true;
    const buildingPermOk =
        item.buildingPermissions !== undefined
            ? hasBuildingPermission(
                  context.buildingPermission ?? null,
                  item.buildingPermissions
              )
            : true;
    const visibilityOk =
        item.visibility !== undefined ? item.visibility() !== false : true;

    return orgPermOk && buildingPermOk && visibilityOk;
}
